import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { KYCLevel2 } from '../types/kyc';
import { ipfsUriToUrl } from '../utils/ipfs';

interface KYCLevel2ReviewFormProps {
  isWhitelisted:boolean;
  data: KYCLevel2;
  status?: number; // KYC Status
  onReview?: (approved: boolean, comments?: string) => Promise<void>;
  submitting?: boolean;
  canReview?: boolean; // Whether there is review permission
  onReset?: () => Promise<void>; 
  canReset?: boolean; // Whether there is reset permission
  reviewStatus?: 'pending' | 'approved' | 'rejected';
}

const KYCLevel2ReviewForm: React.FC<KYCLevel2ReviewFormProps> = ({
  isWhitelisted,
  data,
  status,
  onReview,
  submitting = false,
  canReview = false,
  onReset, 
  canReset = false,
  reviewStatus
}) => {
  const [comments, setComments] = React.useState<string>('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string>('');
  const handleReview = async (approved: boolean) => {
    if (onReview) {
      await onReview(approved, comments);
    }
  };
  const handleReset = async (approved: boolean) => {
    if (onReset) {
      await onReset();
    }
  };
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return 'Pending';
    }
  };
  useEffect(() => {
    if (data) { 
      // If there is an initial video URL, set the preview
      if (data.videoUrl) {
        setVideoPreview(ipfsUriToUrl(data.videoUrl)); 
      }
    } 
  }, [data]);
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Video Verification Review
          </Typography>
          {reviewStatus && (
            <Chip 
              label={getStatusText(reviewStatus)} 
              color={getStatusColor(reviewStatus) as any}
              size="small"
            />
          )}
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          The following is the video verification information submitted by the user. Please carefully verify whether the user clearly speaks the specified random numbers in the video.
        </Alert>
        
        {submitting && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing...
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Random number display - read-only */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
              <Typography variant="subtitle1" gutterBottom>
                The numbers the user needs to say in the video:
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={data.randomNumber || 'Random number not generated'}
                  color="primary"
                  size="medium"
                  sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 1 }}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Please confirm whether the user has clearly and completely spoken these 6 digits in the video.
              </Typography>
            </Card>
          </Grid>
          
          {/* Video display - read-only */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Verification Video
            </Typography>
            
            <Box sx={{ mb: 2 }}>              
              {data.videoUrl ? (
                <Box>
                  <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          ✅ User has uploaded the video
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Please watch the video carefully to confirm whether the user has clearly spoken the random numbers displayed above.
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Video Player */}
                    <Box sx={{ mt: 2 }}>
                      <video
                        src={ipfsUriToUrl(data.videoUrl)}
                        controls
                        style={{ width: '100%', maxWidth: 600, height: 'auto' ,maxHeight:300}}
                      />
                    </Box>
                  </Card>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    User has not uploaded a verification video
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          
          {/* Review Instructions */}
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Review Points:</strong>
                <br />• Confirm that the person in the video is consistent with the previously submitted ID photo
                <br />• Confirm that the user has clearly and completely spoken the specified 6 random digits
                <br />• Confirm that the video is clear and the sound is clear
                <br />• Confirm that the video content is authentic and there are no obvious signs of fraud
                <br />• Confirm that the video duration is reasonable (10-60 seconds is recommended)
              </Typography>
            </Alert>
          </Grid>
          
          {/* Review Function */}
          {canReview   && (
            <>
              
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                     <Button
                                      variant="contained"
                                      color="success"
                                      onClick={() => handleReset(true)}
                                      disabled={submitting||(isWhitelisted&&status==2)}
                                    >
                                      {submitting ? 'Processing...' : 'Reset'}
                                    </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleReview(false)}
                      disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleReview(true)}
                      disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    {submitting ? 'Processing...' : 'Approve'}
                  </Button>
                </Box>
              </Grid>
            </>
          )}
          
          {/* Read-only status prompt */}
          {!canReview && (
            <Grid item xs={12}>
              <Alert severity="info">
                You are viewing the video verification information, which is currently in read-only mode.
              </Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default KYCLevel2ReviewForm;